import { useState } from 'react';
import type { Key } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { enableWarehouseNativeMetricFilters } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY } from '@gonfalon/metrics/constants';
import { DataSourceSelector, DataSourceSelectorTriggerSlot } from '@gonfalon/metrics/DataSourceSelector';
import { ratioAndDuaEnabledFor } from '@gonfalon/metrics/ratioAndDuaEnabledFor';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import {
  Button,
  FieldGroup,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { RequiredLabel } from '../../CreateMetricModal/components/RequiredLabel';
import type { DenominatorForm, MetricForm, RatioTermAggregation, WindowUnit } from '../../CreateMetricModal/formUtils';
import { defaultDenominatorForm } from '../../CreateMetricModal/formUtils';
import { useWinsorizationEnabled } from '../../CreateMetricModal/WinsorizationFields/WinsorizationFields';

import { RemovableSettingSection } from './components/RemovableSettingSection';
import { WarehouseEventQueryField } from './components/WarehouseEventQueryField';
import { EventActivityFormField } from './EventActivityFormField/EventActivityFormField';
import { supportsValueColumn, ValueColumnField } from './ValueColumnField';
import { WindowFieldsRow } from './WindowFieldsRow/WindowFieldsRow';
import { getEndError, getStartError, WINDOW_UNITS, windowUnitLabel } from './windowingUtils';
import { RatioTermWinsorizationFields } from './WinsorizationFields/RatioTermWinsorizationFields';

import styles from './RatioTermSection.module.css';

const VALID_AGGREGATIONS = new Set<RatioTermAggregation>(['count', 'sum', 'average', 'count_distinct']);

function isRatioTermAggregation(key: unknown): key is RatioTermAggregation {
  return typeof key === 'string' && VALID_AGGREGATIONS.has(key as RatioTermAggregation);
}

const aggregationOptions = (): Array<{ id: RatioTermAggregation; label: string; description: string }> => [
  {
    id: 'count',
    label: msg('metrics.metricDefinition.aggregationCount'),
    description: msg('metrics.metricDefinition.countDescription'),
  },
  {
    id: 'count_distinct',
    label: msg('metrics.metricDefinition.aggregationCountDistinct'),
    description: msg('metrics.metricDefinition.countDistinctDescription'),
  },
  {
    id: 'sum',
    label: msg('metrics.metricDefinition.aggregationSum'),
    description: msg('metrics.metricDefinition.ratioSumDescription'),
  },
  {
    id: 'average',
    label: msg('metrics.metricDefinition.aggregationAverage'),
    description: msg('metrics.metricDefinition.ratioAverageDescription'),
  },
];

interface RatioTermSectionProps {
  context: UseFormReturn<MetricForm>;
  label: string;
  isNumerator: boolean;
  /** Renders the add-ons as removable titled sections, as decided by `CreateMetricModal`. */
  useSectionedLayout?: boolean;
  canChangeDataSource?: boolean;
}

export const RatioTermSection: React.FC<RatioTermSectionProps> = ({
  context,
  label,
  isNumerator,
  useSectionedLayout = false,
  canChangeDataSource = true,
}) => {
  const { control, register, setValue, unregister, formState, getValues } = context;
  const [windowOpen, setWindowOpen] = useState(() => {
    if (isNumerator) {
      return getValues('windowMode') === 'custom';
    }
    const denom = getValues('denominator');
    return denom?.windowMode === 'custom';
  });
  const filtersFieldName = isNumerator ? 'filters' : 'denominator.filters';
  const [winsorOpen, setWinsorOpen] = useState(() => {
    if (isNumerator) {
      const lower = getValues('winsorization.lowerPercentile');
      const upper = getValues('winsorization.upperPercentile');
      return lower !== undefined || upper !== undefined;
    }
    const denom = getValues('denominator');
    return Boolean(denom?.winsorLowerEnabled || denom?.winsorUpperEnabled);
  });
  const projectKey = useProjectKey();

  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const dataSources = dataSourcesData?.items?.filter((ds) => Boolean(ds.environmentKey)) ?? [];

  // Field values from different paths depending on numerator vs denominator
  const numeratorDataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const numeratorUnitAggType = useWatch({ control, name: 'unitAggregationType' });
  const numeratorIsNumeric = useWatch({ control, name: 'isNumeric' });
  const numeratorUnitAggField = useWatch({ control, name: 'unitAggregationField' });
  const numeratorWindowUnit = useWatch({ control, name: 'windowUnit' }) ?? 'days';
  const numeratorWindowStart = useWatch({ control, name: 'windowStart' });
  const numeratorWindowEnd = useWatch({ control, name: 'windowEnd' });

  const denominator = useWatch({ control, name: 'denominator' }) as DenominatorForm | undefined;

  // Derive the "Aggregate as" selection for the numerator from its underlying form fields.
  // Cast to string to allow comparison with 'count_distinct', which is outside the current
  // Metric['unitAggregationType'] union while the OpenAPI types catch up.
  function getNumeratorAggregation(): RatioTermAggregation {
    const unitAggTypeStr = numeratorUnitAggType as string | undefined;
    if (unitAggTypeStr === 'count_distinct') {
      return 'count_distinct';
    }
    if (numeratorIsNumeric && unitAggTypeStr === 'sum') {
      return 'sum';
    }
    if (numeratorIsNumeric && unitAggTypeStr === 'average') {
      return 'average';
    }
    return 'count';
  }

  const currentAggregation: RatioTermAggregation = isNumerator
    ? getNumeratorAggregation()
    : (denominator?.aggregationType ?? 'count');

  const currentDataSourceKey = isNumerator ? numeratorDataSourceKey : denominator?.dataSourceKey;
  const currentDataSource = dataSources.find((ds) => ds.key === currentDataSourceKey);
  const currentDataSourceColumns = currentDataSource?.columnMappings?.columns ?? [];
  const currentCountDistinctField = isNumerator
    ? (numeratorUnitAggField ?? '')
    : (denominator?.unitAggregationField ?? '');
  const currentWindowUnit: WindowUnit = isNumerator ? numeratorWindowUnit : (denominator?.windowUnit ?? 'days');
  const currentWindowStart = isNumerator ? numeratorWindowStart : denominator?.windowStart;
  const currentWindowEnd = isNumerator ? numeratorWindowEnd : denominator?.windowEnd;

  const unitDef = WINDOW_UNITS.find((u) => u.id === currentWindowUnit) ?? WINDOW_UNITS[0];
  const maxInUnit = unitDef.maxInUnit;
  const unitLabel = windowUnitLabel(unitDef.id).toLowerCase();

  const isWinsorizationEnabled = useWinsorizationEnabled(currentDataSourceKey);
  const showWinsorizationToggle =
    currentAggregation !== 'count_distinct' && (!currentDataSourceKey || isWinsorizationEnabled);

  // In the sectioned layout an add-on's link is replaced by its section, so only the add-ons
  // that aren't open still have one.
  const showWinsorLink = showWinsorizationToggle && !winsorOpen;
  const showWindowLink = !windowOpen;

  function handleWinsorToggle() {
    const opening = !winsorOpen;
    setWinsorOpen(opening);
    const unregisterOpts = { keepError: false, keepDirty: false, keepTouched: false } as const;
    if (opening) {
      if (isNumerator) {
        // Only write defaults when no existing value is present — preserves real bounds on edit.
        if (getValues('winsorization.lowerPercentile') === undefined) {
          setValue('winsorization.lowerPercentile', 0, { shouldDirty: true });
        }
        if (getValues('winsorization.upperPercentile') === undefined) {
          setValue('winsorization.upperPercentile', 100, { shouldDirty: true });
        }
      } else {
        const existingLower = denominator?.winsorLowerPercentile;
        const existingUpper = denominator?.winsorUpperPercentile;
        setValue(
          'denominator',
          {
            ...(denominator ?? defaultDenominatorForm),
            winsorLowerEnabled: true,
            winsorLowerPercentile: existingLower !== undefined ? existingLower : 0,
            winsorUpperEnabled: true,
            winsorUpperPercentile: existingUpper !== undefined ? existingUpper : 100,
          } as DenominatorForm,
          { shouldDirty: true },
        );
      }
    } else {
      if (isNumerator) {
        unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], unregisterOpts);
      } else {
        unregister(
          ['denominator.winsorLowerPercentile' as never, 'denominator.winsorUpperPercentile' as never],
          unregisterOpts,
        );
        setValue(
          'denominator',
          {
            ...(denominator ?? defaultDenominatorForm),
            winsorLowerEnabled: false,
            winsorLowerPercentile: undefined,
            winsorUpperEnabled: false,
            winsorUpperPercentile: undefined,
          } as DenominatorForm,
          { shouldDirty: true },
        );
      }
    }
  }

  function handleAggregationChange(key: Key | null) {
    if (!isRatioTermAggregation(key)) {
      return;
    }
    const agg = key;
    if (isNumerator) {
      switch (agg) {
        case 'count':
          setValue('isNumeric', false, { shouldDirty: true });
          // Ratio count terms use 'sum' as the wire format — same as simple-metric count path.
          // Sending undefined causes the backend to default to 'average' (occurrence), which
          // rejects winsorization and mis-classifies the metric type.
          setValue('unitAggregationType', 'sum', { shouldDirty: true });
          setValue('unitAggregationField', undefined, { shouldDirty: true });
          break;
        case 'sum':
          setValue('isNumeric', true, { shouldDirty: true });
          setValue('unitAggregationType', 'sum', { shouldDirty: true });
          setValue('unitAggregationField', undefined, { shouldDirty: true });
          break;
        case 'average':
          setValue('isNumeric', true, { shouldDirty: true });
          setValue('unitAggregationType', 'average', { shouldDirty: true });
          setValue('unitAggregationField', undefined, { shouldDirty: true });
          break;
        case 'count_distinct':
          setValue('isNumeric', false, { shouldDirty: true });
          // count_distinct is outside the current Metric['unitAggregationType'] union —
          // cast to preserve the string value while the OpenAPI type catches up.
          setValue('unitAggregationType', 'count_distinct' as MetricForm['unitAggregationType'], {
            shouldDirty: true,
          });
          // Winsorization is incompatible with count_distinct — clear open state and form values.
          if (winsorOpen) {
            setWinsorOpen(false);
            unregister(['winsorization.lowerPercentile', 'winsorization.upperPercentile'], {
              keepError: false,
              keepDirty: false,
              keepTouched: false,
            });
          }
          break;
        default:
          break;
      }
    } else {
      setValue(
        'denominator',
        {
          ...(denominator ?? { eventKey: '', windowMode: 'none', windowUnit: 'days' }),
          aggregationType: agg,
          unitAggregationField: agg === 'count_distinct' ? (denominator?.unitAggregationField ?? '') : undefined,
          // Winsorization is incompatible with count_distinct — clear the flags and values.
          ...(agg === 'count_distinct' && {
            winsorLowerEnabled: false,
            winsorLowerPercentile: undefined,
            winsorUpperEnabled: false,
            winsorUpperPercentile: undefined,
          }),
        },
        { shouldDirty: true },
      );
      if (agg === 'count_distinct' && winsorOpen) {
        setWinsorOpen(false);
      }
    }
  }

  function setDenominatorField<K extends keyof DenominatorForm>(key: K, value: DenominatorForm[K]) {
    setValue(
      'denominator',
      {
        ...(denominator ?? { eventKey: '', aggregationType: 'count', windowMode: 'none', windowUnit: 'days' }),
        [key]: value,
      },
      { shouldDirty: true },
    );
  }

  function handleWindowToggle() {
    const opening = !windowOpen;
    setWindowOpen(opening);
    if (!opening) {
      if (isNumerator) {
        setValue('windowMode', 'none', { shouldDirty: true });
        setValue('windowUnit', 'days', { shouldDirty: true });
        unregister(['windowStart', 'windowEnd'], { keepError: false, keepDirty: false, keepTouched: false });
      } else {
        setValue(
          'denominator',
          {
            ...(denominator ?? { eventKey: '', aggregationType: 'count', windowMode: 'none', windowUnit: 'days' }),
            windowMode: 'none',
            windowUnit: 'days',
            windowStart: undefined,
            windowEnd: undefined,
          },
          { shouldDirty: true },
        );
        // Clearing values alone leaves the offsets' touched/dirty state intact, so reopening
        // the window would immediately re-surface the required-field errors. Unregister to
        // fully reset their validation state and make a reopened window behave like new.
        unregister(['denominator.windowStart', 'denominator.windowEnd'], {
          keepError: false,
          keepDirty: false,
          keepTouched: false,
        });
      }
    } else {
      if (isNumerator) {
        setValue('windowMode', 'custom', { shouldDirty: true });
      } else {
        setDenominatorField('windowMode', 'custom');
      }
    }
  }

  const startVal = isNumerator ? currentWindowStart : denominator?.windowStart;
  const endVal = isNumerator ? currentWindowEnd : denominator?.windowEnd;

  const startError = windowOpen ? getStartError(startVal, maxInUnit, unitLabel) : undefined;
  const endError = windowOpen ? getEndError(endVal, maxInUnit, unitLabel, startVal) : undefined;

  const startTouched = isNumerator
    ? Boolean(formState.touchedFields.windowStart || formState.dirtyFields.windowStart)
    : Boolean(
        (formState.touchedFields.denominator as Record<string, unknown> | undefined)?.windowStart ||
        (formState.dirtyFields.denominator as Record<string, unknown> | undefined)?.windowStart,
      );
  const endTouched = isNumerator
    ? Boolean(formState.touchedFields.windowEnd || formState.dirtyFields.windowEnd)
    : Boolean(
        (formState.touchedFields.denominator as Record<string, unknown> | undefined)?.windowEnd ||
        (formState.dirtyFields.denominator as Record<string, unknown> | undefined)?.windowEnd,
      );

  const showStartInvalid = Boolean(startError) && (startTouched || formState.isSubmitted);
  const showEndInvalid = Boolean(endError) && (endTouched || formState.isSubmitted);

  const showCountDistinctField = currentAggregation === 'count_distinct';
  const isNumericAggregation = currentAggregation === 'sum' || currentAggregation === 'average';
  const showValueColumnField = isNumericAggregation && supportsValueColumn(currentDataSourceKey);

  const showEventFilters =
    enableWarehouseNativeMetricFilters() &&
    currentDataSource?.integrationKey === SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY;

  const showUnsupportedSourceError =
    Boolean(currentDataSource) && !ratioAndDuaEnabledFor(currentDataSource?.integrationKey);

  const selectorProps = {
    selectedKey: currentDataSourceKey,
    dataSources,
    hideSubtitle: true,
    onChange: (dataSource: { key: string }) => {
      if (isNumerator) {
        setValue('dataSourceKey', dataSource.key, { shouldDirty: true });
      } else {
        setDenominatorField('dataSourceKey', dataSource.key);
      }
    },
    selectProps: {
      placeholder: dataSources.length
        ? msg('metrics.metricDefinition.dataSourcePlaceholder')
        : msg('metrics.metricDefinition.noDataSources'),
      isDisabled: !canChangeDataSource,
    },
  };
  // Build the fields only while the window is on: creating the element registers the offsets,
  // which fail their required rule with no visible field to fix.
  const windowFields = windowOpen && (
    <WindowFieldsRow
      variant={useSectionedLayout ? 'section' : 'inline'}
      windowUnit={currentWindowUnit}
      windowStart={currentWindowStart}
      windowEnd={currentWindowEnd}
      showStartInvalid={showStartInvalid}
      showEndInvalid={showEndInvalid}
      startError={startError}
      endError={endError}
      unitLabel={currentWindowUnit}
      maxInUnit={maxInUnit}
      onUnitChange={(unit) => {
        // Clear the offsets via unregister (not setValue) so the fields' touched/dirty
        // state resets too. Otherwise the now-empty Start/End fail their required rule
        // and surface a validation error the instant the unit changes.
        if (isNumerator) {
          setValue('windowUnit', unit, { shouldDirty: true });
          unregister(['windowStart', 'windowEnd'], {
            keepError: false,
            keepDirty: false,
            keepTouched: false,
          });
        } else {
          setValue(
            'denominator',
            {
              ...(denominator ?? {
                eventKey: '',
                aggregationType: 'count',
                windowMode: 'custom',
                windowUnit: 'days',
              }),
              windowUnit: unit,
              windowStart: undefined,
              windowEnd: undefined,
            },
            { shouldDirty: true },
          );
          unregister(['denominator.windowStart', 'denominator.windowEnd'], {
            keepError: false,
            keepDirty: false,
            keepTouched: false,
          });
        }
      }}
      startInputProps={
        isNumerator
          ? register('windowStart', {
              valueAsNumber: true,
              validate: (val) => getStartError(val, maxInUnit, unitLabel) ?? true,
            })
          : register('denominator.windowStart', {
              valueAsNumber: true,
              validate: (val) => getStartError(val, maxInUnit, unitLabel) ?? true,
            })
      }
      endInputProps={
        isNumerator
          ? register('windowEnd', {
              valueAsNumber: true,
              validate: (val, formValues) => getEndError(val, maxInUnit, unitLabel, formValues.windowStart) ?? true,
            })
          : register('denominator.windowEnd', {
              valueAsNumber: true,
              validate: (val, formValues) =>
                getEndError(val, maxInUnit, unitLabel, formValues.denominator?.windowStart) ?? true,
            })
      }
    />
  );
  const winsorizationFields = (
    <RatioTermWinsorizationFields context={context} isNumerator={isNumerator} winsorOpen={winsorOpen} />
  );
  const triggerContent = (
    <>
      <RequiredLabel
        label={msg('metrics.metricDefinition.dataSourceLabel')}
        htmlFor={isNumerator ? 'dataSourceKey' : 'denom-dataSourceKey'}
      />
      <Button>
        <SelectValue />
        <Icon name="chevron-down" size="small" slot="end-icon" />
      </Button>
    </>
  );

  return (
    <div className={styles.termCard}>
      <span className={styles.termLabel}>{label}</span>

      {/* Metric data source */}
      <FieldGroup
        className={styles.fieldGroup}
        errorMessage={showUnsupportedSourceError ? msg('metrics.metricDefinition.ratioUnsupportedSource') : undefined}
      >
        <DataSourceSelector dataSourceVariant="metric" {...selectorProps}>
          <DataSourceSelectorTriggerSlot>{triggerContent}</DataSourceSelectorTriggerSlot>
        </DataSourceSelector>
      </FieldGroup>

      {/* Event key */}
      <EventActivityFormField
        context={context}
        eventKeyPath={isNumerator ? 'eventKey' : 'denominator.eventKey'}
        resolvedDataSourceKey={currentDataSourceKey}
        inputClassName={styles.fullWidth}
      />

      {/* Event property filters */}
      {showEventFilters && (
        <WarehouseEventQueryField context={context} fieldName={filtersFieldName} dataSourceKey={currentDataSourceKey} />
      )}

      {/* Aggregate as */}
      <div className={styles.aggregationRow}>
        <FieldGroup className={styles.fieldGroup}>
          <Controller
            control={control}
            name={isNumerator ? 'unitAggregationType' : 'denominator.aggregationType'}
            render={() => (
              <Select
                onChange={handleAggregationChange}
                value={currentAggregation}
                aria-label={msg('metrics.metricDefinition.aggregateAsLabel')}
                className={styles.aggregationSelect}
              >
                <Label>
                  <Message id="metrics.metricDefinition.aggregateAsLabel" />
                </Label>
                <Button>
                  <SelectValue>
                    {({ isPlaceholder }) =>
                      isPlaceholder
                        ? msg('metrics.metricDefinition.selectPlaceholder')
                        : (aggregationOptions().find((o) => o.id === currentAggregation)?.label ?? '')
                    }
                  </SelectValue>
                  <Icon name="chevron-down" size="small" slot="end-icon" />
                </Button>
                <Popover className={styles.aggregationPopover}>
                  <ListBox items={aggregationOptions()}>
                    {(item) => (
                      <ListBoxItem id={item.id} textValue={item.label} aria-label={item.label}>
                        <div className={styles.aggregationOptionLabel}>
                          <span className={styles.aggregationOptionName}>{item.label}</span>
                          <span className={styles.aggregationOptionDescription}>{item.description}</span>
                        </div>
                      </ListBoxItem>
                    )}
                  </ListBox>
                </Popover>
              </Select>
            )}
          />
        </FieldGroup>

        {showCountDistinctField && (
          <FieldGroup className={`${styles.fieldGroup} ${styles.countDistinctFieldGroup}`}>
            <Controller
              control={control}
              name={isNumerator ? 'unitAggregationField' : 'denominator.unitAggregationField'}
              rules={{ required: msg('metrics.metricDefinition.columnRequired') }}
              render={() => (
                <Select
                  aria-label={msg('metrics.metricDefinition.countDistinctOnLabel')}
                  className={styles.countDistinctField}
                  selectedKey={currentCountDistinctField || null}
                  onSelectionChange={(key) => {
                    const colName = key as string;
                    if (isNumerator) {
                      setValue('unitAggregationField', colName, { shouldDirty: true, shouldValidate: true });
                    } else {
                      setDenominatorField('unitAggregationField', colName);
                    }
                  }}
                  isDisabled={currentDataSourceColumns.length === 0}
                  placeholder={msg('metrics.metricDefinition.columnPlaceholder')}
                >
                  <div className={styles.countDistinctLabelRow}>
                    <RequiredLabel label={msg('metrics.metricDefinition.countDistinctOnLabel')} />
                    <HelpTooltip>
                      <Message id="metrics.metricDefinition.countDistinctHelp" />
                    </HelpTooltip>
                  </div>
                  <Button className={styles.countDistinctTrigger}>
                    <SelectValue />
                    <Icon name="chevron-down" size="small" slot="end-icon" />
                  </Button>
                  <Popover className={styles.countDistinctPopover}>
                    <ListBox items={currentDataSourceColumns}>
                      {(col) => <ListBoxItem id={col.name}>{col.name}</ListBoxItem>}
                    </ListBox>
                  </Popover>
                </Select>
              )}
            />
          </FieldGroup>
        )}

        {showValueColumnField && (
          <FieldGroup className={`${styles.fieldGroup} ${styles.countDistinctFieldGroup}`}>
            <ValueColumnField
              key={currentDataSourceKey}
              name={isNumerator ? 'valueColumn' : 'denominator.valueColumn'}
              dataSourceKey={currentDataSourceKey}
            />
          </FieldGroup>
        )}
      </div>

      {/* Sectioned layout: a link only offers what hasn't been added yet, so the row disappears
       * once both add-ons are open. Removal moves to the X in each section's header. */}
      {(!useSectionedLayout || showWinsorLink || showWindowLink) && (
        <div className={styles.advancedControls}>
          {showWinsorizationToggle && (!useSectionedLayout || showWinsorLink) && (
            <Button variant="minimal" className={styles.expandToggle} onPress={handleWinsorToggle}>
              <Icon
                name={!useSectionedLayout && winsorOpen ? 'minus' : 'add'}
                size="small"
                aria-hidden
                slot="start-icon"
              />
              {!useSectionedLayout && winsorOpen
                ? msg('metrics.winsorization.removeLabel')
                : msg('metrics.winsorization.enableLabel')}
            </Button>
          )}
          {(!useSectionedLayout || showWindowLink) && (
            <Button variant="minimal" className={styles.expandToggle} onPress={handleWindowToggle}>
              <Icon
                name={!useSectionedLayout && windowOpen ? 'minus' : 'add'}
                size="small"
                aria-hidden
                slot="start-icon"
              />
              {!useSectionedLayout && windowOpen
                ? msg('metrics.measurementWindow.removeLabel')
                : msg('metrics.measurementWindow.addLabel')}
            </Button>
          )}
        </div>
      )}
      {/* Measurement window + Winsorization */}
      {(windowOpen || winsorOpen) && (
        <div className={useSectionedLayout ? styles.removableSections : styles.expandSection}>
          {windowOpen &&
            (useSectionedLayout ? (
              <RemovableSettingSection
                title={msg('metrics.measurementWindow.title')}
                removeLabel={msg('metrics.measurementWindow.removeLabel')}
                onRemove={handleWindowToggle}
              >
                {windowFields}
              </RemovableSettingSection>
            ) : (
              windowFields
            ))}
          {winsorOpen &&
            (useSectionedLayout ? (
              <RemovableSettingSection
                title={msg('metrics.winsorization.title')}
                removeLabel={msg('metrics.winsorization.removeLabel')}
                onRemove={handleWinsorToggle}
              >
                {winsorizationFields}
              </RemovableSettingSection>
            ) : (
              winsorizationFields
            ))}
        </div>
      )}
    </div>
  );
};

export { toRatioWindowOffsets } from './windowingUtils';
